<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatAed } from '@shared/utils'

interface Props {
  visible: boolean
  amount: number
  bankName: string
  maskedAccount: string
  remainingBalance: number
  loading: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'confirm' | 'cancel'): void
}>()

const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  if (props.loading) return
  emit('cancel')
}

watch(
  () => props.visible,
  (next) => {
    if (next) {
      window.addEventListener('keydown', handleEscape)
    } else {
      window.removeEventListener('keydown', handleEscape)
    }
  },
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <Teleport to="body">
    <Box
      v-if="visible"
      flex
      align="center"
      justify="center"
      role="dialog"
      aria-modal="true"
      class="withdraw-confirm__overlay"
      @click.self="!loading && emit('cancel')"
    >
      <Box direction="col" gap="4" class="withdraw-confirm">
        <Box align="center" justify="center" class="withdraw-confirm__icon">
          <PiIcon name="arrow-up-right" />
        </Box>

        <Box direction="col" gap="1" align="center">
          <Text type="heading" align="center">Confirm withdrawal?</Text>
          <Text type="body" color="muted" align="center">
            You're about to withdraw
            <Text type="body" weight="semibold" is="span">{{ formatAed(amount) }}</Text>
            to <Text type="body" weight="semibold" is="span">{{ bankName }}</Text>.
          </Text>
        </Box>

        <Box direction="col" gap="2" class="withdraw-confirm__summary">
          <Box align="center" justify="between" gap="3">
            <Text type="body-sm" color="muted" is="span">Amount</Text>
            <Text type="body-sm" weight="semibold" align="right" is="span">
              {{ formatAed(amount) }}
            </Text>
          </Box>
          <Box align="center" justify="between" gap="3">
            <Text type="body-sm" color="muted" is="span">To bank</Text>
            <Text type="body-sm" weight="semibold" align="right" is="span">
              {{ maskedAccount }}
            </Text>
          </Box>
          <Box class="withdraw-confirm__divider" />
          <Box align="center" justify="between" gap="3">
            <Text type="body-sm" color="muted" is="span">Balance after</Text>
            <Text type="body-sm" weight="semibold" align="right" is="span">
              {{ formatAed(remainingBalance) }}
            </Text>
          </Box>
        </Box>

        <Box flex align="center" gap="2" class="withdraw-confirm__note">
          <PiIcon name="clock" class="withdraw-confirm__note-icon" />
          <Text type="caption" color="muted" is="span">
            Funds arrive in your bank within 48 hours.
          </Text>
        </Box>

        <Box flex gap="2" class="withdraw-confirm__actions">
          <Button
            label="Cancel"
            variant="outline"
            color="secondary"
            :disabled="loading"
            class="withdraw-confirm__btn"
            @click="emit('cancel')"
          />
          <Button
            label="Confirm Withdrawal"
            :loading="loading"
            class="withdraw-confirm__btn"
            @click="emit('confirm')"
          />
        </Box>
      </Box>
    </Box>
  </Teleport>
</template>

<style scoped>
.withdraw-confirm__overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  padding: 1.25rem;
  background: var(--p-mask-background, rgba(15, 23, 42, 0.45));
  backdrop-filter: blur(4px);
}

.withdraw-confirm {
  width: 100%;
  max-width: 26rem;
  padding: 1.5rem;
  border-radius: var(--p-border-radius-xl);
  background: var(--p-surface-0);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.withdraw-confirm__icon {
  width: 48px;
  height: 48px;
  border-radius: var(--p-border-radius-md);
  flex-shrink: 0;
  align-self: center;
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200);
  color: var(--p-primary-600);
  --p-icon-size: 20px;
}

.withdraw-confirm__summary {
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  padding: 0.875rem 1rem;
}

.withdraw-confirm__divider {
  height: 1px;
  background: var(--p-surface-200);
  margin: 0.25rem 0;
}

.withdraw-confirm__note {
  justify-content: center;
}

.withdraw-confirm__note-icon {
  color: var(--p-text-muted-color);
  --p-icon-size: 14px;
}

.withdraw-confirm__actions {
  margin-top: 0.25rem;
}

.withdraw-confirm__btn {
  flex: 1;
}
</style>
